import { Injectable, InjectionToken, Provider } from '@angular/core';
import { mockInstitution, mockInstitutionExtended } from '../../mock-data/institution.mock';
import { PaginatedResponse } from '../../shared/pagination/paginated-response';
import {
	ApiInstitution,
	ApiInstitutionActivityResponse,
	ApiInstitutionBranch,
	ApiInstitutionReduced,
	CreateInstitutionApiBody,
	CreateInstitutionPersonApiBody,
} from '../api-types/institution.api';
import { ApiMatch } from '../api-types/match.api';
import {
	CreateInstitution,
	CreateInstitutionPerson,
	Institution,
	InstitutionActivityResponse,
	InstitutionBranch,
	InstitutionExtended,
} from '../app-types/institution.app';
import {
	tranformCreateInstitutionToApiPayload,
	transformApiInstitutionActivityToInstitutionActivity,
	transformApiInstitutionBranchesToAppInstitutionBranches,
	transformApiInstitutionExtendedToInstitutionExtended,
	transformApiInstitutionsToAppInstitutions,
	transformCreateInstitutionPersonToApiBody,
	transformInstitutionExtendedToApiInstitutionExtended,
} from '../transformers/instition.tranform';
import { transformApiMatchToMatch } from '../transformers/match.transform';
import { BaseRequestService } from './base-request.service';

export const INSTITUTION_SERVICE = new InjectionToken<InstitutionProvider>('INSTITUTION_SERVICE');

export interface InstitutionProvider {
	create(req: CreateInstitution): Promise<string>;
	update(institution: InstitutionExtended): Promise<string>;
	getById(id: number | string): Promise<InstitutionExtended>;
	getInstitutionActivity(institutionId: number): Promise<InstitutionActivityResponse>;
	getBranchActivity(institutionId: number, branchId: number): Promise<InstitutionActivityResponse>;
	getAll(offset?: number, range?: number): Promise<PaginatedResponse<Institution>>;
	isAddressUnique(
		streetAndNumber: string,
		postalCodeAndCity: string,
		excludeId?: number,
	): Promise<ApiMatch | undefined>;
	getBranchesOfInstitution(
		id?: number | string,
		searchTerm?: string,
		schoolOnly?: boolean,
		offset?: number,
		range?: number,
	): Promise<PaginatedResponse<InstitutionBranch>>;
	createPerson(req: CreateInstitutionPerson): Promise<{ id: string }>;
}

@Injectable()
export class InstitutionService extends BaseRequestService implements InstitutionProvider {
	public getInstitutionActivity(institutionId: number): Promise<InstitutionActivityResponse> {
		return this.getActivity(`institution/${institutionId}/activity`);
	}

	public getBranchActivity(institutionId: number, branchId: number): Promise<InstitutionActivityResponse> {
		return this.getActivity(`institution/${institutionId}/branches/${branchId}/activity`);
	}

	private async getActivity(url: string): Promise<InstitutionActivityResponse> {
		return this.get<ApiInstitutionActivityResponse>(url).then(transformApiInstitutionActivityToInstitutionActivity);
	}

	/**
	 * Creates a new institution.
	 * @param req - The CreateInstitution object containing institution details.
	 * @returns The response from the API after creating the institution.
	 */
	public async create(req: CreateInstitution) {
		const body = tranformCreateInstitutionToApiPayload(req);
		const response = await this.post<{ id: string }, CreateInstitutionApiBody>('institution', body);
		return response.id;
	}

	public async createPerson(req: CreateInstitutionPerson) {
		const body = transformCreateInstitutionPersonToApiBody(req);

		const result = await this.post<{ id: string }, CreateInstitutionPersonApiBody>(
			`institution/${req.institutionId}/institutionperson`,
			body,
		);
		return result;
	}

	/**
	 * Updates an existing institution.
	 * @param institution - The InstitutionExtended object containing updated institution details.
	 * @returns The ID of the updated institution.
	 */
	public async update(institution: InstitutionExtended) {
		const body = transformInstitutionExtendedToApiInstitutionExtended(institution);

		const response = await this.put<{ id: string }, ApiInstitution>(`institution/${institution.id}`, body);
		return response.id;
	}

	/**
	 * Retrieves a paginated list of institutions.
	 * @param offset - The starting point of the list.
	 * @param range - The number of institutions to retrieve.
	 * @returns A promise resolving to a paginated response of institutions.
	 */
	public async getAll(offset?: number, range?: number): Promise<PaginatedResponse<Institution>> {
		const response = await this.get<PaginatedResponse<ApiInstitutionReduced>>('institution', { offset, range });
		const institutions = transformApiInstitutionsToAppInstitutions(response.entities);
		return {
			...response,
			entities: institutions,
		};
	}

	/**
	 * Retrieves branches of a specific institution.
	 * @param id Unique ID of the institution
	 * @param offset optional, page offset
	 * @param range optional, page range
	 * @returns A paginated version of the branches as result
	 */
	public async getBranchesOfInstitution(
		id?: number | string,
		searchTerm?: string,
		schoolOnly = false,
		offset?: number,
		range?: number,
	) {
		const response = await this.get<PaginatedResponse<ApiInstitutionBranch>>(`institution/branches`, {
			offset,
			range,
			institutionId: id,
			schoolOnly,
			searchTerm,
		});

		const page: PaginatedResponse<InstitutionBranch> = {
			...response,
			entities: transformApiInstitutionBranchesToAppInstitutionBranches(response.entities),
		};
		return page;
	}

	/**
	 * Retrieves an institution by its ID.
	 * @param id - The ID of the institution.
	 * @returns The institution details.
	 */
	public async getById(id: number | string) {
		const response = await this.get<ApiInstitution>(`institution/${String(id)}`);
		return transformApiInstitutionExtendedToInstitutionExtended(response);
	}

	/**
	 * Checks if an address is already in use by another institution.
	 * @param streetAndNumber The street and number to check.
	 * @param postalCodeAndCity Postal code and city name to check.
	 * @param excludeId Optional. The current institution Id, to ignore in editing mode.
	 * @returns a 200 OK status if the address is unique; a 409 status if a duplicated is found and the Id and name of the matching institution.
	 */
	public async isAddressUnique(streetAndNumber: string, postalCodeAndCity: string, excludeId?: number) {
		try {
			const res = await this.get<ApiMatch | undefined>('/validation/isAddressUnique', {
				streetAndNumber: streetAndNumber,
				postalCodeAndCity: postalCodeAndCity,
				number: excludeId,
			});
			return res;
		} catch (error: unknown) {
			const e = error as { status?: number; error?: unknown };
			if (e?.status === 409) {
				return transformApiMatchToMatch(e?.error as ApiMatch);
			}
			throw error;
		}
	}
}

@Injectable()
export class InstitutionMockService extends BaseRequestService implements InstitutionProvider {
	public constructor() {
		super();
		console.warn('⛔️⛔️⛔️ Institution Mock Service: You are currently using a mock service!');
	}
	public async create(_req: CreateInstitution) {
		return 'mock-id';
	}

	public async update(_institution: InstitutionExtended) {
		return 'mock-id';
	}

	public async createPerson(_req: CreateInstitutionPerson): Promise<{ id: string }> {
		return { id: '1' };
	}

	public async getById(_id: number | string): Promise<InstitutionExtended> {
		return mockInstitutionExtended;
	}

	public async getInstitutionActivity(_institutionId: number): Promise<InstitutionActivityResponse> {
		return { activeClientCount: 0, activeProfessionals: [] };
	}

	public async getBranchActivity(_institutionId: number, _branchId: number): Promise<InstitutionActivityResponse> {
		return { activeClientCount: 0, activeProfessionals: [] };
	}

	public async getAll(_offset?: number, _range?: number): Promise<PaginatedResponse<Institution>> {
		return {
			entities: [mockInstitution],
			offset: 0,
			range: 0,
			totalEntities: 1,
		};
	}

	public async isAddressUnique(
		_streetAndNumber: string,
		_postalCodeAndCity: string,
		_excludeId?: number,
	): Promise<ApiMatch | undefined> {
		return undefined;
	}

	public async getBranchesOfInstitution(
		_id?: number | string,
		_searchTerm?: string,
		_schoolOnly = false,
		_offset?: number,
		_range?: number,
	) {
		return {
			entities: [],
			offset: 0,
			range: 0,
			totalEntities: 0,
		};
	}
}

export const provideInstitutionApi: Provider = {
	provide: INSTITUTION_SERVICE,
	useClass: InstitutionService,
};
